import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Newspaper } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import axios, { API, getErrorMessage } from "@/lib/api";
import StatusBadge from "@/components/shared/StatusBadge";
import LoadingScreen from "@/components/shared/LoadingScreen";
import EmptyState from "@/components/shared/EmptyState";
import ConfirmDialog from "@/components/shared/ConfirmDialog";

const CATEGORIES = ["Haji", "Umrah", "Manasik", "Tips Persiapan", "Doa", "Berita KBIHU"];
const EMPTY = { title: "", excerpt: "", content: "", cover_image: "", category: "Haji", author: "Tim KBIHU Muhammadiyah Surabaya", reading_time: 5, status: "draft" };

export default function AdminArtikel() {
  const [items, setItems] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [deleteId, setDeleteId] = useState(null);

  const load = () => axios.get(`${API}/admin/artikel`).then(({ data }) => setItems(data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setModalOpen(true); };
  const openEdit = (a) => { setEditing(a); setForm(a); setModalOpen(true); };
  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handleSubmit = async () => {
    try {
      const payload = { ...form, reading_time: Number(form.reading_time) };
      if (editing) await axios.put(`${API}/admin/artikel/${editing.id}`, payload);
      else await axios.post(`${API}/admin/artikel`, payload);
      toast.success("Artikel berhasil disimpan");
      setModalOpen(false);
      load();
    } catch (e) { toast.error(getErrorMessage(e)); }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/admin/artikel/${deleteId}`);
      toast.success("Artikel berhasil dihapus");
      setDeleteId(null);
      load();
    } catch (e) { toast.error(getErrorMessage(e)); }
  };

  return (
    <div data-testid="admin-artikel-page" className="space-y-5">
      <div className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
        <div>
          <h1 className="font-heading text-2xl font-bold text-[#1F2937]">Artikel</h1>
          <p className="text-sm text-[#64748B]">Kelola konten artikel dan berita KBIHU Muhammadiyah Surabaya.</p>
        </div>
        <Button data-testid="admin-artikel-add-button" onClick={openCreate} className="gap-2 rounded-full bg-[#0F6B4F] text-white hover:bg-[#084C3A]"><Plus className="h-4 w-4" /> Tambah Artikel</Button>
      </div>

      {items === null ? <LoadingScreen /> : items.length === 0 ? (
        <EmptyState icon={Newspaper} title="Belum ada artikel" description="Tambahkan artikel pertama Anda." />
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-100 bg-white">
          <Table data-testid="admin-artikel-table">
            <TableHeader><TableRow><TableHead>Judul</TableHead><TableHead>Kategori</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Aksi</TableHead></TableRow></TableHeader>
            <TableBody>
              {items.map((a) => (
                <TableRow key={a.id} data-testid={`admin-artikel-row-${a.id}`}>
                  <TableCell className="font-medium text-[#1F2937]">{a.title}</TableCell>
                  <TableCell className="text-[#64748B]">{a.category}</TableCell>
                  <TableCell><StatusBadge status={a.status} /></TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" data-testid={`admin-artikel-edit-${a.id}`} onClick={() => openEdit(a)}><Pencil className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" data-testid={`admin-artikel-delete-${a.id}`} onClick={() => setDeleteId(a.id)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-xl" data-testid="admin-artikel-modal">
          <DialogHeader><DialogTitle>{editing ? "Edit Artikel" : "Tambah Artikel"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Judul</Label><Input data-testid="article-form-title" value={form.title} onChange={(e) => update("title", e.target.value)} /></div>
            <div><Label>Ringkasan</Label><Textarea data-testid="article-form-excerpt" value={form.excerpt} onChange={(e) => update("excerpt", e.target.value)} rows={2} /></div>
            <div><Label>Konten</Label><Textarea data-testid="article-form-content" value={form.content} onChange={(e) => update("content", e.target.value)} rows={6} /></div>
            <div><Label>URL Gambar Sampul</Label><Input data-testid="article-form-cover" value={form.cover_image} onChange={(e) => update("cover_image", e.target.value)} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Kategori</Label>
                <Select value={form.category} onValueChange={(v) => update("category", v)}>
                  <SelectTrigger data-testid="article-form-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Waktu Baca (menit)</Label><Input data-testid="article-form-reading-time" type="number" value={form.reading_time} onChange={(e) => update("reading_time", e.target.value)} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Penulis</Label><Input data-testid="article-form-author" value={form.author} onChange={(e) => update("author", e.target.value)} /></div>
              <div><Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => update("status", v)}>
                  <SelectTrigger data-testid="article-form-status"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="draft">Draf</SelectItem><SelectItem value="published">Dipublikasikan</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <Button data-testid="admin-artikel-submit-button" onClick={handleSubmit} className="w-full rounded-full bg-[#0F6B4F] text-white hover:bg-[#084C3A]">Simpan Artikel</Button>
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmDialog open={!!deleteId} onOpenChange={(v) => !v && setDeleteId(null)} title="Hapus Artikel?" onConfirm={handleDelete} testId="admin-artikel-delete-confirm" />
    </div>
  );
}
